import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export type DatasetOption = {
    value: string | undefined;
    label: string | undefined;
    resource_name?: string;
    location_name?: string;
};

export interface PropsType {}

interface DatasetEntry {
    dataset: {
        defaultDatasetId: string | undefined;
        selectedDatasetOption: DatasetOption | undefined;
        selectedDatasetId: string | undefined;
        errors: Record<string, string | undefined>;
    };
    file: {
        selectedFilePath: string | undefined;
        selectedFile: string | undefined;
        defaultFilePath: string | undefined;
        errors: Record<string, string | undefined>;
    };
}

const defaultNewDatasetEntryState: DatasetEntry = {
    dataset: {
        defaultDatasetId: undefined,
        selectedDatasetOption: undefined,
        selectedDatasetId: undefined,
        errors: {},
    },
    file: {
        selectedFilePath: undefined,
        selectedFile: undefined,
        defaultFilePath: undefined,
        errors: {},
    },
};

export type InputDatasetPathState = Record<string, DatasetEntry>;

const initialState: InputDatasetPathState = {};

const createDefaultEntry = (): DatasetEntry => ({
    dataset: {
        ...defaultNewDatasetEntryState.dataset,
        errors: { ...defaultNewDatasetEntryState.dataset.errors },
    },
    file: {
        ...defaultNewDatasetEntryState.file,
        errors: { ...defaultNewDatasetEntryState.file.errors },
    },
});

const inputDatasetPathSlice = createSlice({
    name: 'inputDatasetPath',
    initialState,
    reducers: {
        setDefaultDatasetId: (state, action: PayloadAction<{ datasetKey: string; id: string | undefined }>) => {
            const { datasetKey, id } = action.payload;

            if (!state[datasetKey]) {
                state[datasetKey] = createDefaultEntry();
            }

            if (state[datasetKey]) {
                state[datasetKey].dataset.defaultDatasetId = id;
            }
        },
        setSelectedDatasetOption: (
            state,
            action: PayloadAction<{ datasetKey: string; option: DatasetOption | undefined }>
        ) => {
            const { datasetKey, option } = action.payload;

            if (!state[datasetKey]) {
                state[datasetKey] = createDefaultEntry();
            }

            if (state[datasetKey]) {
                state[datasetKey].dataset.selectedDatasetOption = option;
                state[datasetKey].dataset.selectedDatasetId = option?.value;
            }
        },
        setSelectedDatasetId: (state, action: PayloadAction<{ datasetKey: string; id: string | undefined }>) => {
            const { datasetKey, id } = action.payload;

            if (!state[datasetKey]) {
                state[datasetKey] = createDefaultEntry();
            }

            if (state[datasetKey]) {
                state[datasetKey].dataset.selectedDatasetId = id;
            }
        },
        setSelectedFilePath: (state, action: PayloadAction<{ datasetKey: string; path: string | undefined }>) => {
            const { datasetKey, path } = action.payload;

            if (!state[datasetKey]) {
                state[datasetKey] = createDefaultEntry();
            }

            if (state[datasetKey]) {
                state[datasetKey].file.selectedFilePath = path;
                const filePathParts = path?.split('/');
                const lastPart = filePathParts?.[filePathParts.length - 1];

                if (lastPart && lastPart.length > 0) {
                    state[datasetKey].file.selectedFile = lastPart;
                } else {
                    state[datasetKey].file.selectedFile = undefined;
                }
            }
        },
        setSelectedFile: (state, action: PayloadAction<{ datasetKey: string; fileName: string | undefined }>) => {
            const { datasetKey, fileName } = action.payload;

            if (!state[datasetKey]) {
                state[datasetKey] = createDefaultEntry();
            }

            if (state[datasetKey]) {
                state[datasetKey].file.selectedFile = fileName;
            }
        },
        setFilePathError: (state, action: PayloadAction<{ datasetKey: string; error: string | undefined }>) => {
            const { datasetKey, error } = action.payload;

            if (!state[datasetKey]) {
                state[datasetKey] = createDefaultEntry();
            }

            if (state[datasetKey]) {
                state[datasetKey].file.errors.filePath = error;
            }
        },
        setDefaultFilePath: (state, action: PayloadAction<{ datasetKey: string; path: string | undefined }>) => {
            const { datasetKey, path } = action.payload;

            if (!state[datasetKey]) {
                state[datasetKey] = createDefaultEntry();
            }

            if (state[datasetKey]) {
                state[datasetKey].file.defaultFilePath = path;
            }
        },
        resetSelectedFile: (state, action: PayloadAction<{ datasetKey: string }>) => {
            const { datasetKey } = action.payload;

            if (state[datasetKey]) {
                state[datasetKey].file.selectedFilePath = undefined;
                state[datasetKey].file.selectedFile = undefined;
            }
        },
        resetSelectedDataset: (state, action: PayloadAction<{ datasetKey: string }>) => {
            const { datasetKey } = action.payload;
            if (state[datasetKey]) {
                state[datasetKey].dataset.selectedDatasetOption = undefined;
                state[datasetKey].dataset.selectedDatasetId = undefined;
                state[datasetKey].file.selectedFilePath = undefined;
                state[datasetKey].file.selectedFile = undefined;
            }
        },
        setDatasetError: (
            state,
            action: PayloadAction<{ datasetKey: string; error: string | undefined; errorName: string }>
        ) => {
            const { datasetKey, error, errorName } = action.payload;

            if (!state[datasetKey]) {
                state[datasetKey] = createDefaultEntry();
            }

            if (state[datasetKey]) {
                state[datasetKey].dataset.errors[errorName] = error;
            }
        },
        setFileError: (
            state,
            action: PayloadAction<{ datasetKey: string; error: string | undefined; errorName: string }>
        ) => {
            const { datasetKey, error, errorName } = action.payload;

            if (!state[datasetKey]) {
                state[datasetKey] = createDefaultEntry();
            }

            if (state[datasetKey]) {
                state[datasetKey].file.errors[errorName] = error;
            }
        },

        resetDatasetInputPatState: () => initialState,
    },
});

export const {
    setDefaultDatasetId,
    setSelectedDatasetOption,
    setSelectedDatasetId,
    resetSelectedDataset,
    setSelectedFilePath,
    resetSelectedFile,
    setSelectedFile,
    setDefaultFilePath,
    resetDatasetInputPatState,
    setFilePathError,
    setDatasetError,
} = inputDatasetPathSlice.actions;

export default inputDatasetPathSlice.reducer;
